import { apiClient } from './client'

export interface DashboardTemplateItem {
  id: string
  org_id: string | null
  name: string
  family_key: string
  description: string | null
  source_entry_id: string
  source_workbook_id: string | null
  source_entry_title: string | null
  source_workbook_title: string | null
  version: number
  is_active: boolean
  created_by: string | null
  created_at: string
  updated_at: string
}

export interface DashboardTemplateInstanceItem {
  id: string
  template_id: string
  org_id: string
  target_entry_id: string
  target_workbook_id: string | null
  target_entry_title: string | null
  target_workbook_title: string | null
  status: string
  drift_state: string
  last_rollout_version: number | null
  last_rollout_at: string | null
  last_rollout_by: string | null
  notes: string | null
  created_by: string | null
  created_at: string
  updated_at: string
  rollout_state: string
  rollout_recommended: boolean
  review_required: boolean
  version_delta: number | null
  state_reason: string
  apply_eligible: boolean
  apply_block_reason: string | null
}

export interface DashboardTemplateInstanceSummary {
  total: number
  managed: number
  disabled: number
  unmanaged: number
  in_sync: number
  needs_initial_rollout: number
  behind_source: number
  ahead_of_source: number
  locally_modified: number
  unknown: number
  rollout_recommended: number
  review_required: number
  apply_eligible: number
}

export interface DashboardTemplateDetail extends DashboardTemplateItem {
  instances: DashboardTemplateInstanceItem[]
  instance_summary: DashboardTemplateInstanceSummary
}

export type DashboardTemplateOrgLifecycleState =
  | 'not_provisioned'
  | 'inactive_template'
  | 'disabled'
  | 'unmanaged'
  | 'in_sync'
  | 'needs_initial_rollout'
  | 'behind_source'
  | 'ahead_of_source'
  | 'locally_modified'
  | 'unknown'

export type DashboardTemplateOrgLifecycleAction =
  | 'provision'
  | 'activate_template'
  | 'restore_instance'
  | 'review_instance'
  | 'apply_rollout'
  | 'none'

export interface DashboardTemplateOrgLifecycleItem {
  template_id: string
  template_scope_org_id: string | null
  name: string
  family_key: string
  description: string | null
  source_entry_id: string
  source_workbook_id: string | null
  source_entry_title: string | null
  source_workbook_title: string | null
  version: number
  is_active: boolean
  org_id: string
  org_code: string
  org_name: string
  instance_id: string | null
  target_entry_id: string | null
  target_workbook_id: string | null
  target_entry_title: string | null
  target_workbook_title: string | null
  instance_status: string | null
  drift_state: string | null
  last_rollout_version: number | null
  last_rollout_at: string | null
  rollout_state: string | null
  rollout_recommended: boolean
  review_required: boolean
  version_delta: number | null
  state_reason: string
  apply_eligible: boolean
  apply_block_reason: string | null
  lifecycle_state: DashboardTemplateOrgLifecycleState
  recommended_action: DashboardTemplateOrgLifecycleAction
  action_reason: string
}

export interface DashboardTemplateCreatePayload {
  org_id?: string | null
  name: string
  family_key: string
  description?: string | null
  source_entry_id: string
  source_workbook_id?: string | null
  source_entry_title?: string | null
  source_workbook_title?: string | null
  version?: number
  is_active?: boolean
}

export interface DashboardTemplateUpdatePayload {
  org_id?: string | null
  name?: string
  family_key?: string
  description?: string | null
  source_entry_id?: string
  source_workbook_id?: string | null
  source_entry_title?: string | null
  source_workbook_title?: string | null
  version?: number
  is_active?: boolean
}

export interface DashboardTemplateInstanceCreatePayload {
  org_id: string
  target_entry_id: string
  target_workbook_id?: string | null
  target_entry_title?: string | null
  target_workbook_title?: string | null
  status?: string
  drift_state?: string
  last_rollout_version?: number | null
  notes?: string | null
}

export interface DashboardTemplateInstanceUpdatePayload {
  org_id?: string
  target_entry_id?: string
  target_workbook_id?: string | null
  target_entry_title?: string | null
  target_workbook_title?: string | null
  status?: string
  drift_state?: string
  last_rollout_version?: number | null
  notes?: string | null
}

export type DashboardTemplateRolloutRuntimeMode = 'registry_only' | 'sync_entry_content'
export type DashboardTemplateOrgProvisionMode = 'preview' | 'copy_workbook'

export interface DashboardTemplateOrgProvisionPayload {
  org_ids: string[]
  mode: DashboardTemplateOrgProvisionMode
  collection_id?: string | null
  workbook_title_template?: string | null
  status?: 'managed' | 'unmanaged'
  notes?: string | null
}

export interface DashboardTemplateOrgProvisionItem {
  org_id: string
  org_code: string | null
  org_name: string | null
  existing_instance_id: string | null
  action: 'would_copy_workbook' | 'copied_workbook' | 'skip_existing' | 'blocked'
  reason: string
  applied: boolean
  target_workbook_id: string | null
  target_workbook_title: string | null
  target_entry_id: string | null
  target_entry_title: string | null
  instance_id: string | null
  error: string | null
}

export interface DashboardTemplateOrgProvisionResponse {
  template_id: string
  template_version: number
  mode: DashboardTemplateOrgProvisionMode
  requested_count: number
  applied_count: number
  skipped_count: number
  blocked_count: number
  collection_id: string | null
  items: DashboardTemplateOrgProvisionItem[]
}

export interface DashboardTemplateRolloutApplyPayload {
  mode: 'selected' | 'all_eligible' | 'eligible_by_orgs'
  runtime_mode?: DashboardTemplateRolloutRuntimeMode
  instance_ids?: string[]
  org_ids?: string[]
  note?: string | null
}

export interface DashboardTemplateRolloutApplyItem {
  instance_id: string
  org_id: string
  target_entry_id: string
  target_entry_title: string | null
  applied: boolean
  reason: string
  runtime_applied: boolean
  runtime_reason: string | null
  previous_rollout_state: string
  current_rollout_state: string
  previous_last_rollout_version: number | null
  current_last_rollout_version: number | null
}

export interface DashboardTemplateRolloutApplyResponse {
  template_id: string
  template_version: number
  mode: 'selected' | 'all_eligible' | 'eligible_by_orgs'
  runtime_mode: DashboardTemplateRolloutRuntimeMode
  requested_count: number
  eligible_count: number
  applied_count: number
  skipped_count: number
  runtime_applied_count: number
  runtime_skipped_count: number
  requested_org_ids: string[]
  note: string | null
  items: DashboardTemplateRolloutApplyItem[]
}

export interface DashboardTemplateContentSnapshot {
  entry_id: string
  workbook_id: string | null
  workbook_title: string | null
  entry_name: string | null
  key: string | null
  rev_id: string | null
  scheme_version: number | null
  tabs_count: number
  tab_titles: string[]
  tab_item_counts: number[]
  widget_count: number
  widget_tab_titles: string[]
  item_types: Record<string, number>
  settings_keys: string[]
  signature: string
}

export interface DashboardTemplateCompareItem {
  instance_id: string
  org_id: string
  target_entry_id: string
  target_entry_title: string | null
  rollout_state: string
  apply_eligible: boolean
  compare_status: 'match' | 'different' | 'instance_unavailable'
  mismatch_fields: string[]
  instance_snapshot: DashboardTemplateContentSnapshot | null
  error: string | null
}

export interface DashboardTemplateCompareSummary {
  total: number
  match: number
  different: number
  instance_unavailable: number
}

export interface DashboardTemplateCompareResponse {
  template_id: string
  template_version: number
  source_entry_id: string
  source_status: 'ok' | 'source_unavailable'
  source_snapshot: DashboardTemplateContentSnapshot | null
  source_error: string | null
  summary: DashboardTemplateCompareSummary
  items: DashboardTemplateCompareItem[]
}

export interface DashboardTemplateFamilyRolloutPreviewTemplate {
  template_id: string
  name: string
  family_key: string
  version: number
  is_active: boolean
  source_entry_id: string
  source_entry_title: string | null
  instance_summary: DashboardTemplateInstanceSummary
  eligible_org_ids: string[]
}

export interface DashboardTemplateFamilyRolloutPreviewResponse {
  template_id: string
  family_key: string
  total_templates: number
  active_templates: number
  aggregate_summary: DashboardTemplateInstanceSummary
  eligible_org_ids: string[]
  templates: DashboardTemplateFamilyRolloutPreviewTemplate[]
}

export interface DashboardTemplateFamilyRolloutApplyPayload {
  org_ids: string[]
  note?: string | null
}

export interface DashboardTemplateFamilyRolloutApplyTemplateResult {
  template_id: string
  name: string
  version: number
  requested_count: number
  eligible_count: number
  applied_count: number
  skipped_count: number
}

export interface DashboardTemplateFamilyRolloutApplyResponse {
  template_id: string
  family_key: string
  requested_org_ids: string[]
  template_count: number
  requested_count: number
  eligible_count: number
  applied_count: number
  skipped_count: number
  note: string | null
  template_results: DashboardTemplateFamilyRolloutApplyTemplateResult[]
}

export const fetchDashboardTemplates = async (params?: {
  org_id?: string
  include_inactive?: boolean
}): Promise<DashboardTemplateItem[]> => {
  const response = await apiClient.get<DashboardTemplateItem[]>('/dashboard-templates/', { params })
  return response.data
}

export const fetchDashboardTemplate = async (templateId: string): Promise<DashboardTemplateDetail> => {
  const response = await apiClient.get<DashboardTemplateDetail>(`/dashboard-templates/${templateId}`)
  return response.data
}

export const fetchDashboardTemplateOrgLifecycle = async (params: {
  org_id: string
  include_inactive?: boolean
}): Promise<DashboardTemplateOrgLifecycleItem[]> => {
  const response = await apiClient.get<DashboardTemplateOrgLifecycleItem[]>(
    '/dashboard-templates/org-lifecycle',
    { params },
  )
  return response.data
}

export const createDashboardTemplate = async (
  payload: DashboardTemplateCreatePayload
): Promise<DashboardTemplateItem> => {
  const response = await apiClient.post<DashboardTemplateItem>('/dashboard-templates/', payload)
  return response.data
}

export const updateDashboardTemplate = async (
  templateId: string,
  payload: DashboardTemplateUpdatePayload
): Promise<DashboardTemplateItem> => {
  const response = await apiClient.patch<DashboardTemplateItem>(`/dashboard-templates/${templateId}`, payload)
  return response.data
}

export const deleteDashboardTemplate = async (templateId: string): Promise<void> => {
  await apiClient.delete(`/dashboard-templates/${templateId}`)
}

export const createDashboardTemplateInstance = async (
  templateId: string,
  payload: DashboardTemplateInstanceCreatePayload
): Promise<DashboardTemplateInstanceItem> => {
  const response = await apiClient.post<DashboardTemplateInstanceItem>(
    `/dashboard-templates/${templateId}/instances`,
    payload
  )
  return response.data
}

export const updateDashboardTemplateInstance = async (
  instanceId: string,
  payload: DashboardTemplateInstanceUpdatePayload
): Promise<DashboardTemplateInstanceItem> => {
  const response = await apiClient.patch<DashboardTemplateInstanceItem>(
    `/dashboard-templates/instances/${instanceId}`,
    payload
  )
  return response.data
}

export const deleteDashboardTemplateInstance = async (instanceId: string): Promise<void> => {
  await apiClient.delete(`/dashboard-templates/instances/${instanceId}`)
}

export const provisionDashboardTemplateOrgInstances = async (
  templateId: string,
  payload: DashboardTemplateOrgProvisionPayload,
): Promise<DashboardTemplateOrgProvisionResponse> => {
  const response = await apiClient.post<DashboardTemplateOrgProvisionResponse>(
    `/dashboard-templates/${templateId}/org-provision`,
    payload,
  )
  return response.data
}

export const applyDashboardTemplateRollout = async (
  templateId: string,
  payload: DashboardTemplateRolloutApplyPayload,
): Promise<DashboardTemplateRolloutApplyResponse> => {
  const response = await apiClient.post<DashboardTemplateRolloutApplyResponse>(
    `/dashboard-templates/${templateId}/rollout-apply`,
    payload,
  )
  return response.data
}

export const compareDashboardTemplate = async (
  templateId: string,
): Promise<DashboardTemplateCompareResponse> => {
  const response = await apiClient.get<DashboardTemplateCompareResponse>(
    `/dashboard-templates/${templateId}/compare`,
  )
  return response.data
}

export const previewDashboardTemplateFamilyRollout = async (
  templateId: string,
): Promise<DashboardTemplateFamilyRolloutPreviewResponse> => {
  const response = await apiClient.get<DashboardTemplateFamilyRolloutPreviewResponse>(
    `/dashboard-templates/${templateId}/family-rollout-preview`,
  )
  return response.data
}

export const applyDashboardTemplateFamilyRollout = async (
  templateId: string,
  payload: DashboardTemplateFamilyRolloutApplyPayload,
): Promise<DashboardTemplateFamilyRolloutApplyResponse> => {
  const response = await apiClient.post<DashboardTemplateFamilyRolloutApplyResponse>(
    `/dashboard-templates/${templateId}/family-rollout-apply`,
    payload,
  )
  return response.data
}
